import React, { useState, useEffect } from 'react';
import { X, Mail, Eye, EyeOff, Loader2, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { useAuth } from '../context/AuthContext';

const LoginModal = () => {
  const {
    showLoginModal,
    setShowLoginModal,
    isAuthenticating,
    loginWithGoogle,
    requestEmailOTP,
    verifyEmailOTP,
    loginWithEmail,
    registerUser,
    pendingAction
  } = useAuth();

  const [step, setStep] = useState('choose'); // choose, email-otp, email-password, verify-otp
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [error, setError] = useState('');

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!showLoginModal) {
      setTimeout(() => {
        setStep('choose');
        setEmail('');
        setPassword('');
        setName('');
        setOtp('');
        setError('');
        setIsNewUser(false);
      }, 300);
    }
  }, [showLoginModal]);

  const handleGoogleLogin = () => {
    setError('');
    loginWithGoogle();
  };

  const handleEmailSubmit = async () => {
    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email');
      return;
    }

    setError('');
    const result = await requestEmailOTP(email);
    if (result.success) {
      setStep('verify-otp');
      setIsNewUser(result.data?.is_new_user || false);
    }
  };

  const handleOTPVerify = async () => {
    if (!otp.trim() || otp.length < 4) {
      setError('Please enter a valid OTP');
      return;
    }

    setError('');
    const result = await verifyEmailOTP(email, otp, isNewUser ? name : null);
    if (!result.success) {
      setError(result.error || 'Invalid OTP');
    }
  };

  const handlePasswordLogin = async () => {
    if (!email.trim() || !password.trim()) {
      setError('Please enter email and password');
      return;
    }

    setError('');
    const result = await loginWithEmail(email, password);
    if (!result.success) {
      setError(result.error || 'Login failed');
    }
  };

  const handleRegister = async () => {
    if (!email.trim() || !password.trim() || !name.trim()) {
      setError('Please fill in all fields');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setError('');
    const result = await registerUser(email, password, name);
    if (!result.success) {
      setError(result.error || 'Registration failed');
    }
  };

  const goBack = () => {
    setStep('choose');
    setError('');
    setOtp('');
  };

  return (
    <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden">
        <div className="p-6">
          <DialogHeader className="mb-6">
            <DialogTitle className="font-serif text-2xl text-center">
              {step === 'choose' && 'Welcome to WVC'}
              {step === 'email-otp' && 'Login with Email'}
              {step === 'verify-otp' && 'Enter OTP'}
              {step === 'email-password' && 'Login / Sign Up'}
            </DialogTitle>
            <DialogDescription className="text-center">
              {step === 'choose' && (pendingAction ? `Login to ${pendingAction.type}` : 'Login to access all features')}
              {step === 'email-otp' && "We'll send you a one-time code"}
              {step === 'verify-otp' && `Code sent to ${email}`}
              {step === 'email-password' && 'Use your email and password'}
            </DialogDescription>
          </DialogHeader>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
              {error}
            </div>
          )}

          {/* Step: Choose Method */}
          {step === 'choose' && (
            <div className="space-y-4">
              {/* Google Login */}
              <Button
                onClick={handleGoogleLogin}
                disabled={isAuthenticating}
                variant="outline"
                className="w-full h-12 border-stone-300 hover:bg-stone-50"
              >
                {isAuthenticating ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Continue with Google
                  </>
                )}
              </Button>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-stone-200" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-3 text-stone-500">Or</span>
                </div>
              </div>

              {/* Email OTP Option */}
              <Button
                onClick={() => setStep('email-otp')}
                variant="outline"
                className="w-full h-12 border-stone-300 hover:bg-stone-50"
              >
                <Mail className="w-5 h-5 mr-3" />
                Continue with Email (OTP)
              </Button>

              {/* Email Password Option */}
              <button
                onClick={() => setStep('email-password')}
                className="w-full text-sm text-stone-500 hover:text-stone-700 mt-2"
              >
                Login with password instead
              </button>
            </div>
          )}

          {/* Step: Email OTP Entry */}
          {step === 'email-otp' && (
            <div className="space-y-4">
              <button onClick={goBack} className="flex items-center text-sm text-stone-500 hover:text-stone-700 mb-4">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </button>
              
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12"
                autoFocus
                disabled={isAuthenticating}
              />
              
              <Button
                onClick={handleEmailSubmit}
                disabled={isAuthenticating || !email.trim()}
                className="w-full h-12 bg-stone-900 hover:bg-stone-800"
              >
                {isAuthenticating ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Send OTP'
                )}
              </Button>
            </div>
          )}

          {/* Step: Verify OTP */}
          {step === 'verify-otp' && (
            <div className="space-y-4">
              <button onClick={goBack} className="flex items-center text-sm text-stone-500 hover:text-stone-700 mb-4">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </button>

              {isNewUser && (
                <Input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-12"
                  disabled={isAuthenticating}
                />
              )}
              
              <Input
                type="text"
                placeholder="Enter 6-digit OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="h-12 text-center text-2xl tracking-widest"
                maxLength={6}
                autoFocus
                disabled={isAuthenticating}
              />
              
              <Button
                onClick={handleOTPVerify}
                disabled={isAuthenticating || otp.length < 4}
                className="w-full h-12 bg-stone-900 hover:bg-stone-800"
              >
                {isAuthenticating ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Verify & Login'
                )}
              </Button>

              <button
                onClick={handleEmailSubmit}
                disabled={isAuthenticating}
                className="w-full text-sm text-stone-500 hover:text-stone-700"
              >
                Resend OTP
              </button>
            </div>
          )}

          {/* Step: Email Password */}
          {step === 'email-password' && (
            <div className="space-y-4">
              <button onClick={goBack} className="flex items-center text-sm text-stone-500 hover:text-stone-700 mb-4">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </button>

              <Input
                type="text"
                placeholder="Your name (for new accounts)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-12"
                disabled={isAuthenticating}
              />
              
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12"
                disabled={isAuthenticating}
              />
              
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 pr-10"
                  disabled={isAuthenticating}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={handlePasswordLogin}
                  disabled={isAuthenticating}
                  variant="outline"
                  className="flex-1 h-12"
                >
                  {isAuthenticating ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Login'}
                </Button>
                <Button
                  onClick={handleRegister}
                  disabled={isAuthenticating}
                  className="flex-1 h-12 bg-stone-900 hover:bg-stone-800"
                >
                  {isAuthenticating ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign Up'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default LoginModal;
